import React, { useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import { Alert } from "@mui/material";
import Button from "../components/UI/Button";
import { ProgressContainer } from "../components/UI/ProgressContainer";
import { useHouseContext } from "../HouseContext";
import { httpsCallable } from "firebase/functions";
import { fireFunctions } from "../firebase-config";

type OccupancyResult = {
  year: number;
  houseName: string;
  totalOccupancy: number;
  totalNights: number;
  totalReservations: number;
  capacity?: number;
  maxPossibleOvernights?: number;
  occupancyRate?: number | null;
};

const PageOccupancy: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const houseCtx = useHouseContext();

  const currentYear = new Date().getFullYear();

  const years = useMemo(() => {
    const arr: number[] = [];
    for (let y = currentYear; y >= currentYear - 10; y--) arr.push(y);
    return arr;
  }, [currentYear]);

  const [selectedHouse, setSelectedHouse] = useState<string>(Array.from(houseCtx.houses.values())[0].houseName);
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [result, setResult] = useState<OccupancyResult | null>(null);

  const fetchOccupancy = async () => {
    if (!selectedHouse) {
      enqueueSnackbar("Bitte ein Haus wählen", { variant: "warning", persist: false,
        anchorOrigin: { vertical: "top", horizontal: "center" } });
      return;
    }
    setIsLoading(true);
    setResult(null);
    try {
      const getYearlyOccupancy = httpsCallable(fireFunctions, "getYearlyOccupancy");
      const resp = await getYearlyOccupancy({ year: selectedYear, houseName: selectedHouse });
      setResult(resp.data as OccupancyResult);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Fehler beim Abrufen der Auslastung", { variant: "error", persist: false,
        anchorOrigin: { vertical: "top", horizontal: "center" } });
    } finally {
      setIsLoading(false);
    }
  };

  const pctText =
    result && typeof result.occupancyRate === "number"
      ? `${(result.occupancyRate * 100).toFixed(2)} %`
      : result && result.occupancyRate === null
      ? "n/a"
      : "-";

  return (
    <ProgressContainer isLoading={isLoading}>
      <div className="p-4">
        <h1 className="text-2xl mt-0 mb-6 text-gray-700">Auslastung</h1>
        <Alert severity="warning" className="mb-4">
          Berechnung für Buchungen ab Februar 2026 korrekt. Buchungen davor können doppelt gezählt werden und ergeben daher eine falsche höhere Auslastung.
        </Alert>

          <div className="flex flex-wrap items-end gap-4 mb-6">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Haus</label>
              <select
                className="border rounded px-3 py-2 min-w-[220px]"
                value={selectedHouse}
                onChange={(e) => setSelectedHouse(e.target.value)}
              >
                {Array.from(houseCtx.houses.values()).map((house, idx) => (
                  <option key={idx} value={house.houseName}>
                    {house.houseName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">Jahr</label>
              <select
                className="border rounded px-3 py-2 min-w-[120px]"
                value={String(selectedYear)}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Button onClick={fetchOccupancy} disabled={isLoading}>
                OK
              </Button>
            </div>
          </div>

          <div>
            {!result ? (
              <p className="text-gray-500">Wähle Haus und Jahr und klicke OK, um die Auslastung anzuzeigen.</p>
            ) : (
              <div className="bg-white p-4 rounded shadow-sm">
                <h2 className="text-lg font-semibold mb-3">
                  Resultate für {result.houseName} - {result.year}
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-700">
                  <div>Übernachtungen (Anzahl Gäste * Nächte):</div>
                  <div className="font-medium">{result.totalOccupancy ?? 0}</div>

                  <div>Anzahl Reservierungen:</div>
                  <div className="font-medium">{result.totalReservations ?? 0}</div>

                  <div>Hauskapazität pro Tag:</div>
                  <div className="font-medium">{result.capacity ?? "unbekannt"}</div>

                  <div>Maximale Übernachtungen (Hauskapazität * 365):</div>
                  <div className="font-medium">{result.maxPossibleOvernights ?? "unbekannt"}</div>

                  <div>Auslastungsrate (Übernachtungen/Maximale Übernachtungen):</div>
                  <div className="font-medium">{pctText}</div>
                </div>
              </div>
            )}
          </div>
      </div>
    </ProgressContainer>
  );
};

export default PageOccupancy;
